import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceProjectResource } from "../../common/projectResource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  project: Boundaries["retrieveProjectResources"]["request"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { project, request: setRequestStateToPending(), projectResources: [] },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    projectResources,
  }: Boundaries["retrieveProjectResources"]["success"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          projectResources,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { project, request: setRequestStateToSuccess(), projectResources },
      ];
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveProjectResources"]["failure"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { project, request: setRequestStateToFailure(), projectResources: [] },
      ];
}

function handleMaterialResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectMaterialResource"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === projectResource.project.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          projectResources: [...res.projectResources, projectResource],
        }
      : res,
  );
}

function handleVirtualResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectVirtualResource"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === projectResource.project.uuid
      ? { ...res, projectResources: [...res.projectResources, projectResource] }
      : res,
  );
}

function handleResourceFromProjectRemoval(
  state: State,
  {
    projectResource,
    project,
  }: Boundaries["removeResourceFromProject"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          projectResources: res.projectResources.filter(
            ({ uuid }) => uuid !== projectResource.uuid,
          ),
        }
      : res,
  );
}

function handleRemoveResource(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return state.map((res) => ({
    ...res,
    request: setRequestStateToSuccess(),
    projectResources: res.projectResources.filter(
      (projectResource) => resource.uuid !== projectResource.resource.uuid,
    ),
  }));
}

function handleAddPlanzoneResourceToProjectSuccess(
  state: State,
  {
    projectResource,
    project,
  }: Boundaries["addPlanzoneResourceToProject"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, projectResources: [...res.projectResources, projectResource] }
      : res,
  );
}

function handleAddPlanzoneResourcesToProjectSuccess(
  state: State,
  {
    projectResources,
    project,
  }: Boundaries["addPlanzoneResourcesToProject"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          projectResources: [...res.projectResources, ...projectResources],
        }
      : res,
  );
}

function handleDisableResource(
  state: State,
  projectResource: Boundaries["disableProjectResource"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === projectResource.project.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          projectResources: res.projectResources.map((resource) =>
            resource.uuid === projectResource.uuid
              ? { ...resource, deactivated: projectResource.deactivated }
              : resource,
          ),
        }
      : res,
  );
}

function handleActivateResource(
  state: State,
  projectResource: Boundaries["activateProjectResource"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === projectResource.project.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          projectResources: res.projectResources.map((resource) =>
            resource.uuid === projectResource.uuid
              ? { ...resource, deactivated: projectResource.deactivated }
              : resource,
          ),
        }
      : res,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    projectResources: item.projectResources.map((projectResource) =>
      reduceProjectResource(projectResource, event),
    ),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectResourcesRequested]: handleRequest,
      [events.retrieveProjectResourcesSucceeded]: handleSuccess,
      [events.retrieveProjectResourcesFailed]: handleFailure,
      [events.addProjectMaterialResourceSucceeded]:
        handleMaterialResourceAddition,
      [events.addProjectVirtualResourceSucceeded]:
        handleVirtualResourceAddition,
      [events.removeResourceFromProjectSucceeded]:
        handleResourceFromProjectRemoval,
      [events.addPlanzoneResourceToProjectSucceeded]:
        handleAddPlanzoneResourceToProjectSuccess,
      [events.removeResourceFromPlanzoneSucceeded]: handleRemoveResource,
      [events.addPlanzoneResourcesToProjectSucceeded]:
        handleAddPlanzoneResourcesToProjectSuccess,
      [events.addEveryoneToProjectSucceeded]:
        handleAddPlanzoneResourcesToProjectSuccess,
      [events.disableProjectResourceSucceeded]: handleDisableResource,
      [events.activateProjectResourceSucceeded]: handleActivateResource,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
